import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Compass,
  MapPin,
  Users,
  Building2,
  TrendingUp,
} from "lucide-react";
import { api } from "@/lib/api";
import RankedSchoolRow from "@/components/RankedSchoolRow";
import HeroSlideshow from "@/components/HeroSlideshow";

export default function Home() {
  const [stats, setStats] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api
      .get("/rankings?limit=10")
      .then((r) => setRankings(r.data.items || []))
      .catch(() => {});
    api
      .get("/districts")
      .then((r) => setDistricts(r.data.districts || []))
      .catch(() => {});
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    navigate(`/browse${params.toString() ? `?${params}` : ""}`);
  };

  return (
    <div>
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, #FDFBF7 0%, #F7F1E7 100%)",
        }}
      >
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none">
          <div
            className="absolute inset-0 bg-repeat"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #1E3A2B 1px, transparent 0)",
              backgroundSize: "28px 28px",
            }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-14 md:pt-28 md:pb-20">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-[#1E3A2B] text-[#DAA520] text-xs uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full mb-6">
              <TrendingUp className="w-3.5 h-3.5" />
              2026 Edition
            </div>
            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-stone-900"
              data-testid="home-hero-title"
            >
              The <span className="text-[#C85A32]">25 best schools</span> in
              Abuja, ranked.
            </h1>
            <p
              className="mt-6 text-base sm:text-lg text-stone-700 max-w-3xl leading-relaxed"
              data-testid="home-hero-lead"
            >
              The best school in Abuja is{" "}
              <strong className="text-stone-900">
                Whiteplains British School
              </strong>
              . It ranks No. 1 among the 25 best schools in Abuja for 2026,
              teaching the British National Curriculum through Cambridge IGCSE
              and A-Level from its campus in Jabi, Abuja. It is followed by{" "}
              Loyola Jesuit College, the American International School Abuja and
              the British Nigerian Academy.
            </p>
            <p className="mt-4 text-sm sm:text-base text-stone-600 max-w-3xl leading-relaxed">
              An independent review of the best private and international
              schools in Abuja, Nigeria — assessed on curriculum, examination
              outcomes, teaching, facilities and where graduates go next.
            </p>

            <form
              onSubmit={onSubmit}
              className="mt-8 bg-white rounded-2xl border border-stone-200 p-2 flex items-center gap-2 shadow-[0_12px_40px_-16px_rgba(0,0,0,0.15)] max-w-2xl"
            >
              <div className="flex items-center flex-1 pl-3">
                <Search className="w-5 h-5 text-stone-400 shrink-0" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  type="text"
                  placeholder="Search by school name, district or keyword…"
                  data-testid="search-input-hero"
                  className="flex-1 px-3 py-3 text-base bg-transparent focus:outline-none placeholder-stone-400"
                />
              </div>
              <button
                type="submit"
                data-testid="search-submit-hero"
                className="bg-[#C85A32] hover:bg-[#A84624] text-white font-semibold px-5 py-3 rounded-xl transition-colors shrink-0"
              >
                Search
              </button>
            </form>

            <div className="mt-6 flex flex-wrap gap-2">
              <span className="text-xs text-stone-500 self-center mr-1">
                Popular:
              </span>
              {["Maitama", "Gwarinpa", "Jabi", "Lugbe", "Wuse 2", "Kubwa"].map(
                (d) => (
                  <Link
                    key={d}
                    to={`/browse?district=${encodeURIComponent(d)}`}
                    data-testid={`hero-chip-${d.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-xs font-medium bg-white border border-stone-200 text-stone-700 hover:border-[#C85A32] hover:text-[#C85A32] px-3 py-1.5 rounded-full transition-colors"
                  >
                    {d}
                  </Link>
                )
              )}
            </div>
          </div>

          {/* Dynamic slideshow */}
          <div className="mt-10 md:mt-14">
            <HeroSlideshow schools={rankings.slice(0, 6)} />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
          data-testid="home-stats-grid"
        >
          {[
            {
              label: "Verified schools",
              value: stats?.approved_schools ?? "—",
              icon: Building2,
            },
            {
              label: "Districts covered",
              value: stats?.districts_covered ?? "—",
              icon: MapPin,
            },
            {
              label: "Private schools",
              value: stats?.private_schools ?? "—",
              icon: Users,
            },
            {
              label: "Public schools",
              value: stats?.public_schools ?? "—",
              icon: Compass,
            },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-white rounded-2xl border border-stone-200 p-5"
              data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <s.icon className="w-5 h-5 text-[#C85A32] mb-3" />
              <div className="font-display text-3xl font-bold text-stone-900">
                {s.value}
              </div>
              <div className="text-xs uppercase tracking-wider text-stone-500 mt-1 font-semibold">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Rankings */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16"
        data-testid="rankings-section"
      >
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C85A32]">
              The ranking
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-1 leading-tight">
              Top 10 schools in Abuja — 2026
            </h2>
            <p className="mt-2 text-stone-600 max-w-2xl">
              The top-ranked private and international schools in the Federal
              Capital Territory this year, ordered by overall standing.
            </p>
          </div>
          <Link
            to="/browse"
            data-testid="rankings-browse-all"
            className="text-sm font-semibold text-[#1E3A2B] hover:text-[#C85A32] inline-flex items-center gap-1"
          >
            Browse all schools →
          </Link>
        </div>
        <div className="space-y-4" data-testid="ranked-schools-list">
          {rankings.length === 0 ? (
            <div className="grid gap-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-stone-200 p-6 h-40 animate-pulse"
                />
              ))}
            </div>
          ) : (
            rankings.map((s) => <RankedSchoolRow key={s.id} school={s} />)
          )}
        </div>
      </section>

      {/* Districts */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C85A32]">
          By district
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 mt-1 mb-6">
          Browse by Abuja district
        </h2>
        <div className="flex flex-wrap gap-2" data-testid="districts-cloud">
          {districts.map((d) => (
            <Link
              key={d}
              to={`/browse?district=${encodeURIComponent(d)}`}
              className="px-4 py-2 rounded-full bg-white border border-stone-200 text-sm text-stone-700 hover:border-[#1E3A2B] hover:bg-[#1E3A2B] hover:text-white transition-colors font-medium"
            >
              {d}
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
        <div className="relative bg-[#1E3A2B] rounded-3xl p-8 md:p-14 overflow-hidden">
          <div
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #DAA520 1px, transparent 0)",
              backgroundSize: "24px 24px",
            }}
          />
          <div className="relative max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#DAA520]">
              For schools
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mt-2">
              Run a school in Abuja?
            </h2>
            <p className="text-stone-200 mt-3 leading-relaxed">
              List your institution on Best Schools in Abuja for free. We
              review every submission and publish verified entries so parents
              can discover you.
            </p>
            <Link
              to="/submit"
              data-testid="home-cta-submit"
              className="mt-6 inline-block bg-[#C85A32] hover:bg-[#A84624] text-white font-semibold px-6 py-3 rounded-xl transition-colors"
            >
              Submit your school
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
